import { useEffect, useMemo, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Palette, ExternalLink, Upload, Plus, Trash2 } from "lucide-react";
import { THEME_ICON_OPTIONS, ThemeGlyph } from "@/lib/themeIcons";

export default function SuperThemeSettings() {
  const [tab, setTab] = useState("merchants");
  const [items, setItems] = useState([]);
  const [themes, setThemes] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ switch_price: 499, themes: [], categories: [] });
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/platform/theme-settings"),
      api.get("/platform/themes"),
      api.get("/platform/theme-catalog"),
    ]).then(([tenants, th, cat]) => {
      setItems(tenants.data || []);
      setThemes(th.data?.themes || []);
      setCatalog(cat.data || { switch_price: 499, themes: [], categories: [] });
    }).catch((e) => toast.error(extractErr(e))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((t) =>
      `${t.name || ""} ${t.slug || ""} ${t.storefront_theme || ""}`.toLowerCase().includes(s)
    );
  }, [items, q]);

  const setTheme = async (t, themeId) => {
    if (t.storefront_theme === themeId) return;
    setSaving((m) => ({ ...m, [t.id]: true }));
    try {
      await api.put(`/platform/tenants/${t.id}/theme`, { storefront_theme: themeId });
      setItems((list) => list.map((x) => (x.id === t.id ? { ...x, storefront_theme: themeId } : x)));
      toast.success(`${t.name} → ${themeId}`);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving((m) => ({ ...m, [t.id]: false }));
    }
  };

  const saveCatalog = async () => {
    setCatalogSaving(true);
    try {
      const { data } = await api.put("/platform/theme-catalog", catalog);
      setCatalog(data);
      toast.success("Theme catalog saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setCatalogSaving(false);
    }
  };

  const setThemeField = (id, key, value) => {
    setCatalog((c) => ({
      ...c,
      themes: (c.themes || []).map((t) => (t.id === id ? { ...t, [key]: value } : t)),
    }));
  };

  const toggleThemeCategory = (themeId, catId) => {
    setCatalog((c) => ({
      ...c,
      themes: (c.themes || []).map((t) => {
        if (t.id !== themeId) return t;
        const cur = t.category_ids || [];
        const next = cur.includes(catId) ? cur.filter((x) => x !== catId) : [...cur, catId];
        return { ...t, category_ids: next };
      }),
    }));
  };

  const addCategory = () => {
    const n = (catalog.categories || []).length + 1;
    setCatalog((c) => ({
      ...c,
      categories: [...(c.categories || []), { id: `category-${n}`, name: `Category ${n}`, icon: "layout-grid" }],
    }));
  };

  const setCategoryField = (idx, key, value) => {
    setCatalog((c) => ({
      ...c,
      categories: (c.categories || []).map((row, i) => (i === idx ? { ...row, [key]: value } : row)),
    }));
  };

  const removeCategory = (idx) => {
    setCatalog((c) => {
      const categories = (c.categories || []).filter((_, i) => i !== idx);
      const removed = (c.categories || [])[idx]?.id;
      return {
        ...c,
        categories,
        themes: (c.themes || []).map((t) => ({
          ...t,
          category_ids: (t.category_ids || []).filter((id) => id !== removed),
        })),
      };
    });
  };

  const uploadAsset = async (themeId, field, file) => {
    if (!file) return;
    const key = `${themeId}-${field}`;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/theme-catalog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setThemeField(themeId, field, data.url);
      toast.success("Image uploaded — save More settings to keep it");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setUploading("");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-100 flex items-center gap-2">
          <Palette className="w-7 h-7 text-indigo-300" /> Theme Settings
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Assign a merchant theme for free from Merchants, or set the paid switch fee and preview assets under More.
        </p>
      </div>

      <div className="flex gap-2" data-testid="theme-settings-tabs">
        <Button
          type="button"
          size="sm"
          variant={tab === "merchants" ? "default" : "outline"}
          className={tab === "merchants" ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border-slate-700 text-slate-200"}
          onClick={() => setTab("merchants")}
          data-testid="theme-tab-merchants"
        >
          Merchants
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "more" ? "default" : "outline"}
          className={tab === "more" ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border-slate-700 text-slate-200"}
          onClick={() => setTab("more")}
          data-testid="theme-tab-more"
        >
          More
        </Button>
      </div>

      {tab === "more" ? (
        <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-6">
          <div>
            <label className="text-sm font-medium text-slate-300">Theme switch amount (₹)</label>
            <p className="text-xs text-slate-500 mt-1 mb-2">Charged to the merchant each time they switch from Theme Store. GST is added on top.</p>
            <Input
              type="number"
              min="0"
              step="1"
              value={catalog.switch_price ?? 0}
              onChange={(e) => setCatalog((c) => ({ ...c, switch_price: e.target.value }))}
              className="bg-slate-950 border-slate-700 text-slate-100 max-w-xs"
              data-testid="theme-switch-price"
            />
          </div>

          <div className="space-y-3" data-testid="theme-categories-editor">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-slate-300">Marketplace categories</div>
                <p className="text-xs text-slate-500 mt-1">Shown on /theme. Assign an icon, then tick which themes belong to each category.</p>
              </div>
              <Button type="button" size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={addCategory} data-testid="theme-category-add">
                <Plus className="w-4 h-4 mr-1" /> Add category
              </Button>
            </div>
            {(catalog.categories || []).length === 0 ? (
              <p className="text-sm text-slate-500">No categories yet. Add one to group themes on the public marketplace.</p>
            ) : (
              <div className="space-y-3">
                {(catalog.categories || []).map((cat, idx) => (
                  <div key={`${cat.id}-${idx}`} className="rounded-xl border border-slate-800 p-3 grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end" data-testid={`theme-category-row-${idx}`}>
                    <div>
                      <label className="text-xs text-slate-400">Name</label>
                      <Input
                        value={cat.name || ""}
                        onChange={(e) => setCategoryField(idx, "name", e.target.value)}
                        className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                        data-testid={`theme-category-name-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Icon</label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-9 h-9 rounded-lg bg-slate-800 text-indigo-300 inline-flex items-center justify-center shrink-0">
                          <ThemeGlyph name={cat.icon} className="w-4 h-4" />
                        </span>
                        <select
                          value={THEME_ICON_OPTIONS.some((o) => o.id === cat.icon) ? cat.icon : "layout-grid"}
                          onChange={(e) => setCategoryField(idx, "icon", e.target.value)}
                          className="flex-1 h-10 rounded-md bg-slate-950 border border-slate-700 text-slate-100 text-sm px-2"
                          data-testid={`theme-category-icon-${idx}`}
                        >
                          {THEME_ICON_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="border-slate-700 text-rose-300" onClick={() => removeCategory(idx)} data-testid={`theme-category-remove-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            {(catalog.themes || []).map((th) => (
              <div key={th.id} className="rounded-xl border border-slate-800 p-4 space-y-3" data-testid={`theme-catalog-${th.id}`}>
                <div className="font-semibold">{th.label}</div>
                <div>
                  <label className="text-xs text-slate-400">Thumbnail URL</label>
                  <Input
                    value={th.thumbnail_url || ""}
                    onChange={(e) => setThemeField(th.id, "thumbnail_url", e.target.value)}
                    placeholder="https://…"
                    className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                    data-testid={`theme-thumb-${th.id}`}
                  />
                  <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-indigo-300 cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> {uploading === `${th.id}-thumbnail_url` ? "Uploading…" : "Upload thumbnail"}
                    <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadAsset(th.id, "thumbnail_url", e.target.files[0])} data-testid={`theme-thumb-file-${th.id}`} />
                  </label>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Full-page screenshot (onboarding picker)</label>
                  <Input
                    value={th.screenshot_url || ""}
                    onChange={(e) => setThemeField(th.id, "screenshot_url", e.target.value)}
                    placeholder="https://…"
                    className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                    data-testid={`theme-screenshot-${th.id}`}
                  />
                  <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-indigo-300 cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> {uploading === `${th.id}-screenshot_url` ? "Uploading…" : "Upload full-page screenshot"}
                    <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadAsset(th.id, "screenshot_url", e.target.files[0])} data-testid={`theme-screenshot-file-${th.id}`} />
                  </label>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Live preview link</label>
                  <Input
                    value={th.preview_url || ""}
                    onChange={(e) => setThemeField(th.id, "preview_url", e.target.value)}
                    placeholder="https://…"
                    className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                    data-testid={`theme-preview-url-${th.id}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Marketplace icon</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="w-9 h-9 rounded-lg bg-slate-800 text-indigo-300 inline-flex items-center justify-center">
                      <ThemeGlyph name={th.icon} className="w-4 h-4" />
                    </span>
                    <select
                      value={THEME_ICON_OPTIONS.some((o) => o.id === th.icon) ? th.icon : "layout-grid"}
                      onChange={(e) => setThemeField(th.id, "icon", e.target.value)}
                      className="flex-1 h-10 rounded-md bg-slate-950 border border-slate-700 text-slate-100 text-sm px-2"
                      data-testid={`theme-icon-${th.id}`}
                    >
                      {THEME_ICON_OPTIONS.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Tags (comma separated)</label>
                  <Input
                    value={Array.isArray(th.tags) ? th.tags.join(", ") : (th.tags || "")}
                    onChange={(e) => setThemeField(th.id, "tags", e.target.value)}
                    placeholder="Business, Modern"
                    className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                    data-testid={`theme-tags-${th.id}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Badge</label>
                  <select
                    value={th.badge || ""}
                    onChange={(e) => setThemeField(th.id, "badge", e.target.value)}
                    className="w-full h-10 rounded-md bg-slate-950 border border-slate-700 text-slate-100 text-sm px-2 mt-1"
                    data-testid={`theme-badge-${th.id}`}
                  >
                    <option value="">None</option>
                    <option value="popular">Popular</option>
                    <option value="new">New</option>
                    <option value="premium">Premium</option>
                    <option value="free">Free</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Categories</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(catalog.categories || []).map((cat) => {
                      const on = (th.category_ids || []).includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => toggleThemeCategory(th.id, cat.id)}
                          className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-indigo-600 border-indigo-500 text-white" : "border-slate-700 text-slate-300"}`}
                          data-testid={`theme-cat-assign-${th.id}-${cat.id}`}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                    {(catalog.categories || []).length === 0 ? <span className="text-xs text-slate-500">Add categories above first.</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button type="button" onClick={saveCatalog} disabled={catalogSaving} data-testid="theme-catalog-save">
            {catalogSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save More settings
          </Button>
        </Card>
      ) : (
        <>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search merchant name or slug…"
            className="bg-slate-950 border-slate-700 text-slate-100 max-w-md"
            data-testid="theme-settings-search"
          />
          <Card className="bg-slate-900 border-slate-800 p-0 overflow-hidden text-slate-100">
            {loading ? (
              <div className="p-8 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading merchants…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No merchants found.</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {filtered.map((t) => (
                  <div key={t.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`theme-row-${t.slug}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{t.name}</div>
                      <div className="text-xs text-slate-500 font-mono-nums">/{t.slug} · {t.status}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {themes.map((th) => {
                        const sel = (t.storefront_theme || "classic") === th.id;
                        return (
                          <Button
                            key={th.id}
                            type="button"
                            size="sm"
                            disabled={!!saving[t.id]}
                            variant={sel ? "default" : "outline"}
                            className={sel ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border-slate-700 text-slate-200"}
                            onClick={() => setTheme(t, th.id)}
                            data-testid={`theme-set-${t.slug}-${th.id}`}
                          >
                            {saving[t.id] && sel ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                            {th.label}
                          </Button>
                        );
                      })}
                      <a href={`/${t.slug}`} target="_blank" rel="noreferrer" className="text-xs text-indigo-300 inline-flex items-center gap-1 px-2">
                        Preview <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
